import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Users, Megaphone } from "lucide-react";

const NONE = "none";

function Composer({ followers, onSent }) {
  const [msg, setMsg] = useState("");
  const [wid, setWid] = useState(NONE);
  const [ws, setWs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/business/workshops").then((r) => setWs(r.data.filter((w) => w.active && w.publication_status !== "pending"))).catch(() => {}); }, []);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/business/broadcasts", { message: msg, workshop_id: wid === NONE ? null : wid });
      toast.success(`Aviso enviado a ${followers} seguidor(es)`); setMsg(""); setWid(NONE); onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="broadcast-composer">
      <p className="font-bold flex items-center gap-1.5"><Megaphone className="w-4 h-4 text-primary" />Avisar a mis seguidores</p>
      <Textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 500))} rows={4} placeholder="Ej: ¡Abrí una nueva sesión este sábado! Quedan pocos cupos." data-testid="broadcast-message" />
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={wid} onValueChange={setWid}>
          <SelectTrigger className="sm:flex-1" data-testid="broadcast-workshop"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Sin enlace a un taller</SelectItem>
            {ws.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={send} disabled={busy || msg.trim().length < 3 || !followers} className="rounded-full" data-testid="broadcast-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Enviar</>}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">{followers ? `Llega al instante como aviso en el teléfono y aparece en «Mis Maestros» de tus ${followers} seguidor(es). ${msg.length}/500` : "Aún no tienes seguidores: cuando un alumno te siga podrás avisarle."}</p>
    </Card>
  );
}

export function MaestroComunidad() {
  const [f, setF] = useState(null);
  const [posts, setPosts] = useState([]);
  const loadPosts = () => api.get("/business/broadcasts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { api.get("/business/followers").then((r) => setF(r.data)).catch(() => setF({ count: 0, new_7d: 0, items: [] })); loadPosts(); }, []);
  if (!f) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-8" />;
  return (
    <div className="pt-4 grid lg:grid-cols-[1fr_320px] gap-5" data-testid="maestro-comunidad">
      <div className="space-y-5 min-w-0">
        <Composer followers={f.count} onSent={loadPosts} />
        <div>
          <h3 className="font-bold mb-2.5">Avisos enviados</h3>
          <div className="space-y-2.5" data-testid="broadcast-history">
            {posts.map((p) => (
              <Card key={p.id} className="p-3" data-testid={`broadcast-${p.id}`}>
                <p className="text-sm whitespace-pre-line break-words">{p.message}</p>
                <p className="text-[11px] text-muted-foreground mt-1.5">{formatDateTime(p.created_at)} · {p.followers} seguidor(es){p.workshop_name ? ` · ${p.workshop_name}` : ""}</p>
              </Card>
            ))}
            {posts.length === 0 && <p className="text-sm text-muted-foreground">Todavía no envías avisos.</p>}
          </div>
        </div>
      </div>
      <Card className="p-4 h-fit" data-testid="followers-card">
        <p className="font-bold flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" />Seguidores</p>
        <p className="text-3xl font-extrabold mt-2" data-testid="followers-count">{f.count}</p>
        <p className="text-xs text-muted-foreground">{f.new_7d ? `+${f.new_7d} en los últimos 7 días` : "Sin nuevos esta semana"}</p>
        <div className="divide-y mt-3 max-h-80 overflow-y-auto">
          {f.items.map((x, i) => <div key={i} className="py-2 flex justify-between gap-2 text-sm"><span className="truncate">{x.name}</span><span className="text-[11px] text-muted-foreground shrink-0">{formatDateTime(x.since).split(",")[0]}</span></div>)}
        </div>
      </Card>
    </div>
  );
}
